import {
  manuscriptContentFromPlainText,
  normalizeManuscriptContent,
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";

import {
  TEXTBOOK_RICH_MANUSCRIPT_BLOCK_TYPES,
} from "./textbookRichManuscriptBridge";

const RICH_TYPES =
  new Set(
    TEXTBOOK_RICH_MANUSCRIPT_BLOCK_TYPES
  );

const BOUNDARY_PREFIX =
  "__nextscenes_textbook_boundary__";

function exactText(value) {
  return value == null
    ? ""
    : String(value);
}

export function textbookChapterBoundaryId(
  index
) {
  return `${BOUNDARY_PREFIX}${index}`;
}

export function textbookChapterBoundaryIndex(
  value
) {
  const id =
    exactText(value);

  if (
    !id.startsWith(
      BOUNDARY_PREFIX
    )
  ) {
    return -1;
  }

  const index =
    Number(
      id.slice(
        BOUNDARY_PREFIX.length
      )
    );

  return Number.isInteger(index) &&
    index >= 0
      ? index
      : -1;
}

export function isTextbookChapterRichBlock(
  block
) {
  return RICH_TYPES.has(
    exactText(
      block?.type
    ).trim()
  );
}

function richContentForBlock(
  block
) {
  if (
    !isTextbookChapterRichBlock(
      block
    )
  ) {
    return null;
  }

  const candidate =
    block?.manuscriptContent &&
    typeof block
      .manuscriptContent ===
      "object" &&
    !Array.isArray(
      block.manuscriptContent
    )
      ? block.manuscriptContent
      : manuscriptContentFromPlainText(
          exactText(
            block?.text
          )
        );

  const normalized =
    normalizeManuscriptContent(
      candidate
    );

  if (
    normalized.ok &&
    normalized.content
  ) {
    return normalized.content;
  }

  return manuscriptContentFromPlainText(
    exactText(
      block?.text
    )
  );
}

function uniqueChapterBlock(
  block,
  usedIds,
  outerIndex,
  innerIndex
) {
  const requested =
    exactText(
      block?.id
    ).trim();

  let id =
    requested ||
    `textbook-${outerIndex + 1}-${innerIndex + 1}`;

  if (
    usedIds.has(id) &&
    id.startsWith(
      "proposal-adoption-"
    )
  ) {
    throw new Error(
      `Duplicate Textbook proposal-adoption marker: ${id}`
    );
  }

  if (usedIds.has(id)) {
    const base =
      `textbook-${outerIndex + 1}-${innerIndex + 1}`;

    id = base;
    let suffix = 2;

    while (usedIds.has(id)) {
      id =
        `${base}-${suffix}`;
      suffix += 1;
    }
  }

  usedIds.add(id);

  return {
    ...block,
    id,
  };
}

export function buildTextbookChapterEditorContent(
  value
) {
  const source =
    Array.isArray(value)
      ? value
      : [];

  const blocks = [];
  const usedIds =
    new Set();

  source.forEach(
    (block, outerIndex) => {
      const marker =
        textbookChapterBoundaryId(
          outerIndex
        );

      usedIds.add(marker);

      blocks.push({
        id: marker,
        type:
          "page_break",
      });

      const content =
        richContentForBlock(
          block
        );

      if (!content) {
        return;
      }

      content.blocks.forEach(
        (
          innerBlock,
          innerIndex
        ) => {
          blocks.push(
            uniqueChapterBlock(
              innerBlock,
              usedIds,
              outerIndex,
              innerIndex
            )
          );
        }
      );
    }
  );

  const normalized =
    normalizeManuscriptContent({
      version: 1,
      blocks,
    });

  if (
    !normalized.ok ||
    !normalized.content
  ) {
    throw new Error(
      "The Textbook chapter could not be composed into one manuscript editor."
    );
  }

  return normalized.content;
}

function emptyRichContent(
  block,
  index
) {
  const source =
    richContentForBlock(
      block
    );

  const first =
    source?.blocks?.find(
      (item) =>
        item?.type !==
        "page_break"
    );

  return {
    version: 1,
    blocks: [
      first
        ? {
            ...first,
            id:
              exactText(
                first.id
              ).trim() ||
              `textbook-empty-${index + 1}`,
            runs: [
              {
                text: "",
              },
            ],
          }
        : {
            id:
              `textbook-empty-${index + 1}`,
            type:
              "paragraph",
            runs: [
              {
                text: "",
              },
            ],
          },
    ],
  };
}

export function applyTextbookChapterEditorContent(
  originalBlocks,
  manuscriptContent
) {
  const source =
    Array.isArray(
      originalBlocks
    )
      ? originalBlocks
      : [];

  const normalized =
    normalizeManuscriptContent(
      manuscriptContent
    );

  if (
    !normalized.ok ||
    !normalized.content
  ) {
    return source;
  }

  const byIndex =
    new Map();

  let activeIndex = -1;

  normalized.content.blocks.forEach(
    (block) => {
      const boundaryIndex =
        block?.type ===
          "page_break"
          ? textbookChapterBoundaryIndex(
              block.id
            )
          : -1;

      if (boundaryIndex >= 0) {
        activeIndex =
          boundaryIndex;

        if (
          !byIndex.has(
            activeIndex
          )
        ) {
          byIndex.set(
            activeIndex,
            []
          );
        }

        return;
      }

      if (activeIndex < 0) {
        return;
      }

      byIndex
        .get(activeIndex)
        ?.push(block);
    }
  );

  return source.map(
    (block, index) => {
      if (
        !isTextbookChapterRichBlock(
          block
        )
      ) {
        return block;
      }

      const segmentBlocks =
        byIndex.get(index) ||
        [];

      const candidate =
        segmentBlocks.length
          ? {
              version: 1,
              blocks:
                segmentBlocks,
            }
          : emptyRichContent(
              block,
              index
            );

      const segment =
        normalizeManuscriptContent(
          candidate
        );

      if (
        !segment.ok ||
        !segment.content
      ) {
        return block;
      }

      return {
        ...block,
        text:
          plainTextFromManuscriptContent(
            segment.content
          ),
        manuscriptContent:
          segment.content,
      };
    }
  );
}
